import { isAiDraftPending } from "../components/workbench/estimate/composerModel";

export const DEFAULT_NEW_ESTIMATE_CHANGE_NOTE = "Initial estimate created.";
export const DEFAULT_UPDATE_ESTIMATE_CHANGE_NOTE = "Estimate updated";

const JOB_UI_ONLY_FIELDS = [
  "availableMaterials",
  "availableProductTypes",
  "materialsLoading",
  "materialsError",
  "pricing_templates",
  "default_client_pricing_tier",
  // Bookkeeping for "the line array changed shape" — see formReducer's
  // initialState. It must not reach job_data, the calculate body, the calc
  // snapshot or the dirty snapshot: a remove-then-re-add that restores
  // identical content still has to read as clean, and staleness stays
  // content-based.
  "line_items_revision",
];

export const buildPersistedJobPayload = (formData = {}) => {
  const payload = { ...formData };
  JOB_UI_ONLY_FIELDS.forEach((field) => {
    delete payload[field];
  });
  return payload;
};

/**
 * Admin per-job burden override is active only as a positive number.
 * Blank, 0, and non-numeric values leave the settings-mirrored rate in place.
 */
export const positiveJobLaborBurdenOverride = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The body POSTed to /api/calculate.
 *
 * An AI draft that the estimator has not accepted yet is NOT part of the
 * estimate: it is a proposal sitting in the rail waiting for a human to sign
 * off. So it is stripped here, at the payload boundary, rather than guarded at
 * each of the several call sites that price the form. Two things follow, both
 * intended:
 *
 *   1. Inserting a draft does not change this payload, so the debounced
 *      auto-recalculate does not fire on it and the pending line never reaches
 *      the pricing engine at all.
 *   2. Pressing Calculate with a pending draft in the job prices everything
 *      except that line.
 *
 * Accepting the draft puts it back in — from that moment it is an ordinary
 * line and behaves exactly like a hand-typed one.
 *
 * NOTE: this is deliberately NOT the snapshot used to decide whether the form
 * is dirty. A pending draft is unsaved work; closing the tab on one should
 * still warn.
 *
 * Job burden override: the admin override means "price THIS estimate's fab
 * labor at this rate", full stop. New estimates are pinned to the burden rate
 * (the labor-rate-type selector was retired 2026-08-18), but a JobVersion
 * saved before that can still carry laborRateType "island" or "custom", and
 * the engine still honors those legacy values — resolving a different payload
 * key per mode (burden → labor_burden_rate, island →
 * island_millwork_labor_rate, custom → labor_custom_rate). So the override is
 * written onto ALL THREE here rather than switching on the mode: whatever mode
 * an old payload resolves to, the rate is the override. Writing only
 * labor_burden_rate was Greptile PR#24's finding — custom-mode estimates
 * silently ignored the entered rate; do not narrow this back to one key while
 * legacy payloads exist. Persist keeps the override as its own field and never
 * touches the settings-mirrored rates.
 */
export const buildCalculationPayload = (formState = {}) => {
  const payload = buildPersistedJobPayload(formState);
  const override = positiveJobLaborBurdenOverride(
    payload.job_labor_burden_override,
  );
  const withRate = override
    ? {
        ...payload,
        labor_burden_rate: override,
        island_millwork_labor_rate: override,
        labor_custom_rate: override,
      }
    : payload;
  if (!Array.isArray(withRate.line_items)) return withRate;
  return {
    ...withRate,
    line_items: withRate.line_items.filter((item) => !isAiDraftPending(item)),
  };
};

/**
 * Put the priced results back in step with the form's line rows.
 *
 * `line_item_results` is matched to lines POSITIONALLY (JobContentsRail,
 * EstimateWorkbench index into it). Holding a pending AI
 * draft out of the payload shortens that array, which would slide every result
 * after the draft onto the wrong row — a line showing another line's money.
 * So a `null` placeholder is spliced back in wherever a pending draft sits: the
 * draft row shows no figures, which is exactly right, and every other row keeps
 * its own.
 */
export const alignLineItemResults = (results, lineItems) => {
  if (!results || !Array.isArray(results.line_item_results)) return results;
  if (!Array.isArray(lineItems) || !lineItems.some(isAiDraftPending)) {
    return results;
  }

  const priced = results.line_item_results;
  let cursor = 0;
  const aligned = lineItems.map((item) => {
    if (isAiDraftPending(item)) return null;
    const result = cursor < priced.length ? priced[cursor] : null;
    cursor += 1;
    return result;
  });

  return { ...results, line_item_results: aligned };
};

/**
 * The priced results for a saved JobVersion, computed WITHOUT touching state.
 *
 * Opening a job has to be all-or-nothing: the save target and the form contents
 * move together or not at all. Building the results inline used to break that —
 * a version with no `calculation_results` and a malformed `job_data` threw while
 * mapping `line_items`, but only AFTER the job and the form had already been
 * committed, leaving Save pointed at a job whose contents were never loaded.
 * Doing the work here, ahead of the first setter, means the only thing that can
 * fail is a fetch, and a failed fetch commits nothing.
 *
 * Returns `{ results, fromCalculation }`; `fromCalculation` tells the caller
 * whether it may treat the results as a real priced run (and so snapshot them
 * for stale detection) or as a reconstruction from stored totals.
 */
export const buildVersionResults = (version) => {
  const jobData = (version && version.job_data) || {};

  if (version && version.calculation_results) {
    return {
      results: alignLineItemResults(
        version.calculation_results,
        jobData.line_items,
      ),
      fromCalculation: true,
    };
  }

  // No stored calculation: rebuild what we can from the form's own totals. A
  // version with no line_items at all is odd but not fatal — an empty estimate
  // opens empty rather than throwing on the way in.
  const lineItems = Array.isArray(jobData.line_items) ? jobData.line_items : [];
  return {
    results: {
      line_item_results: lineItems.map((item) => ({
        ...item,
        calculated_cost: item?.calculated_cost || 0,
        calculated_sqft: item?.calculated_sqft || 0,
      })),
      base_cost: jobData.total_materials_cost || 0,
      labor_cost: jobData.total_labor_cost || 0,
      overhead_cost: jobData.overhead_cost || 0,
      break_even_cost: jobData.break_even_cost || 0,
      profit_amount: jobData.profit_amount || 0,
      total_job_cost: jobData.total_job_cost || 0,
      final_price: jobData.final_price || 0,
    },
    fromCalculation: false,
  };
};
